import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import ChannelModal from '../components/ChannelModal';
import ChannelUsersModal from '../components/ChannelUsersModal';

// Icons
import { ReactComponent as EditIcon } from '../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as ChevronLeftIcon } from '../assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../assets/icons/chevron-right.svg';
import { ReactComponent as ChevronDoubleLeftIcon } from '../assets/icons/chevron-double-left.svg';
import { ReactComponent as ChevronDoubleRightIcon } from '../assets/icons/chevron-double-right.svg';
import { ReactComponent as SortUpIcon } from '../assets/icons/sort-up.svg';
import { ReactComponent as SortDownIcon } from '../assets/icons/sort-down.svg';
import { ReactComponent as SortIcon } from '../assets/icons/sort.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as CloseIcon } from '../assets/icons/close.svg';
import { ReactComponent as UsersIcon } from '../assets/icons/users.svg';

const Channels = () => {
  const [channels, setChannels] = useState([]);
  const [allChannels, setAllChannels] = useState([]); // Store all channels for client-side filtering
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modals
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showChannelUsersModal, setShowChannelUsersModal] = useState(false);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalChannels, setTotalChannels] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Sorting state
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin';
  
  // Fetch channels on component mount or when sorting changes
  useEffect(() => {
    fetchChannels();
  }, [sortField, sortDirection]);
  
  const fetchChannels = async () => {
    try {
      setIsLoading(true);
      // Modified API call to include sorting parameters
      const response = await apiClient.get('/channels', {
        params: {
          sortBy: sortField,
          sortDir: sortDirection
        }
      });
      
      // Handling response
      const channelsData = response.data.channels || response.data;
      setAllChannels(channelsData);
      
      setError(null);
    } catch (error) {
      console.error('Error fetching channels:', error);
      setError('Failed to load channels. Please try again later.');
      setAllChannels([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filter and paginate channels based on search term and current page
  const filteredChannels = useMemo(() => {
    if (!searchTerm.trim()) {
      return allChannels;
    }
    
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    
    return allChannels.filter(channel => 
      (channel.channel_name && channel.channel_name.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (channel.channel_id && channel.channel_id.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (channel.loan_number && channel.loan_number.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (channel.loan_status && channel.loan_status.toLowerCase().includes(lowerCaseSearchTerm))
    );
  }, [allChannels, searchTerm]);
  
  // Calculate pagination based on filtered channels
  const paginatedChannels = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return filteredChannels.slice(startIndex, endIndex);
  }, [filteredChannels, currentPage, pageSize]);
  
  // Update pagination info whenever filtered channels change
  useEffect(() => {
    setTotalChannels(filteredChannels.length);
    setTotalPages(Math.ceil(filteredChannels.length / pageSize));
    
    // Reset to first page if current page is out of bounds
    if (currentPage > Math.ceil(filteredChannels.length / pageSize)) {
      setCurrentPage(1);
    }
  }, [filteredChannels, pageSize]);
  
  // Update displayed channels whenever paginated channels change
  useEffect(() => {
    setChannels(paginatedChannels);
  }, [paginatedChannels]);
  
  const handleSort = (field) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as sort field with default asc direction
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };
  
  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  const renderSortIcon = (field) => {
    if (field !== sortField) {
      return <SortIcon className="w-4 h-4 ml-1 inline-block text-gray-400" />;
    }
    
    return sortDirection === 'asc' 
      ? <SortUpIcon className="w-4 h-4 ml-1 inline-block text-primary-600" />
      : <SortDownIcon className="w-4 h-4 ml-1 inline-block text-primary-600" />;
  };
  
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };
  
  const handleAddChannel = () => {
    setCurrentChannel(null);
    setShowChannelModal(true);
  };
  
  const handleEditChannel = (channel) => {
    setCurrentChannel(channel);
    setShowChannelModal(true);
  };
  
  const handleDeleteClick = (channel) => {
    setChannelToDelete(channel);
    setIsDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!channelToDelete) return;
    
    try {
      await apiClient.delete(`/channels/${channelToDelete.id}`);
      
      // After successful delete, refresh the channel list
      fetchChannels();
      
      setIsDeleteConfirmOpen(false);
      setChannelToDelete(null);
    } catch (error) {
      console.error('Error deleting channel:', error);
      setError('Failed to delete channel. Please try again.');
    }
  };
  
  const handleModalSave = async (channelData, isNew) => {
    try {
      if (isNew) {
        // Create new channel
        await apiClient.post('/channels', channelData);
        // Refresh to reflect the changes
        fetchChannels();
      } else {
        // Update existing channel
        await apiClient.put(`/channels/${channelData.id}`, channelData);
        
        // Update in allChannels array
        setAllChannels(allChannels.map(channel => 
          channel.id === channelData.id ? { ...channel, ...channelData } : channel
        ));
      }
      setShowChannelModal(false);
    } catch (error) {
      console.error('Error saving channel:', error);
      setError('Failed to save channel. Please try again.');
    }
  };
  
  const handleShowChannelUsers = (channel) => {
    setCurrentChannel(channel);
    setShowChannelUsersModal(true);
  };
  
  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Generate page numbers for pagination control
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Show at most 5 page numbers
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };
  
  // Column headers with sortable fields
  const columns = [
    { id: 'id', label: 'ID', sortable: true },
    { id: 'channel_name', label: 'Channel Name', sortable: true },
    { id: 'channel_id', label: 'Channel ID', sortable: true },
    { id: 'loan_number', label: 'Loan Number', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'loan_status', label: 'Loan Status', sortable: true },
    { id: 'created_at', label: 'Created At', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false }
  ];
  
  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Manage Channels</h1>
        
        {isAdmin && (
          <button
            onClick={handleAddChannel}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add New Channel</span>
          </button>
        )}
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Search and pagination controls row */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
        {/* Search input */}
        <div className="relative w-full md:w-72">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search channels..."
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              title="Clear search"
            >
              <CloseIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
            </button>
          )}
        </div>
        
        {/* Page size selector */}
        <div className="flex items-center">
          <label htmlFor="pageSize" className="mr-2 text-sm text-gray-600">
            Rows per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={handlePageSizeChange}
            className="border rounded-md border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
      
      {/* Search results indicator */}
      {searchTerm && (
        <div className="mb-4 text-sm text-gray-600">
          Found {filteredChannels.length} {filteredChannels.length === 1 ? 'channel' : 'channels'} matching "{searchTerm}"
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(column => (
                    <th 
                      key={column.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column.sortable ? (
                        <button 
                          className="flex items-center cursor-pointer focus:outline-none"
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                          {renderSortIcon(column.id)}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {channels.length === 0 ? (
                  <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    {searchTerm ? 'No matching channels found' : 'No channels found'}
                  </td>
                </tr>
              ) : (
                channels.map((channel) => (
                  <tr key={channel.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{channel.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{channel.channel_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{channel.channel_id || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{channel.loan_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        channel.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {channel.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{channel.loan_status || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(channel.created_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2 items-center">
                        <button
                          onClick={() => handleShowChannelUsers(channel)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                          title="View Channel Users"
                        >
                          <UsersIcon className="w-5 h-5" />
                        </button>
                        
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleEditChannel(channel)}
                              className="text-primary-600 hover:text-primary-900 transition-colors"
                              title="Edit Channel"
                            >
                              <EditIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(channel)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Delete Channel"
                            >
                              <DeleteIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination controls */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {channels.length ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, totalChannels)} of {totalChannels} channels
          </div>
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button 
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
              title="First Page"
            >
              <ChevronDoubleLeftIcon className="w-5 h-5" />
            </button>
            
            {/* Previous Page Button */}
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
              title="Previous Page"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            
            {/* Page Number Buttons */}
            {getPageNumbers().map(pageNumber => (
              <button
                key={pageNumber}
                onClick={() => handlePageChange(pageNumber)}
                className={`px-3 py-1 rounded-md ${
                  pageNumber === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-primary-50'
                }`}
              >
                {pageNumber}
              </button>
            ))}
            
            {/* Next Page Button */}
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
              title="Next Page"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:bg-primary-50'}`}
              title="Last Page"
            >
              <ChevronDoubleRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Channel Modal for Add/Edit */}
    {showChannelModal && (
      <ChannelModal
        channel={currentChannel}
        onClose={() => setShowChannelModal(false)}
        onSave={handleModalSave}
      />
    )}
    
    {/* Channel Users Modal */}
    {showChannelUsersModal && (
      <ChannelUsersModal
        channel={currentChannel}
        onClose={() => setShowChannelUsersModal(false)}
      />
    )}
    
    {/* Delete Confirmation Modal */}
    {isDeleteConfirmOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
          <p className="text-gray-700 mb-6">
            Are you sure you want to delete channel <span className="font-semibold">{channelToDelete?.channel_name}</span>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default Channels;